import type { ReactNode } from 'react';
import { useMemo } from 'react';
import SvgIcon from '@mui/material/SvgIcon';
import { paths } from '../paths';
import PeopleIcon from '@mui/icons-material/People';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import BikeScooterIcon from '@mui/icons-material/BikeScooter';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import SettingsIcon from '@mui/icons-material/Settings';
import DescriptionIcon from '@mui/icons-material/Description';
import WysiwygIcon from '@mui/icons-material/Wysiwyg';
import CardMembershipIcon from '@mui/icons-material/CardMembership';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import { Badge } from '@mui/material';
import { userPermissions } from '../util/userPermissions';
import { findAccess } from '../utils';
import { ISystem } from '../ts/interface/system.interface';
import { useNavigationContext } from '../context/navigation-context';
import { useOrganizationDetailRelayQuery } from '../hooks/organization/query/V2/useOrganizationDetailRelay';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import LockClockIcon from '@mui/icons-material/LockClock';
import BuildIcon from '@mui/icons-material/Build';
import ChatIcon from '@mui/icons-material/Chat';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import useOrganization from '../hooks/use-organization';
import { useSupportChatUnread } from '../hooks/chat/useSupportChatUnread';
import { useChatAccess } from '../hooks/chat/useChatAccess';
export interface Item {
  disabled?: boolean;
  external?: boolean;
  icon?: ReactNode;
  items?: Item[];
  label?: ReactNode;
  path?: string;
  title: string;
}

export interface Section {
  items: Item[];
  subheader?: string;
}

const {
  BASIC,
  USERS,
  RENTAL,
  PHYSICAL,
  LOCATIONS,
  SUPPORT,
  MAINTENANCE,
  MEMBERSHIPS,
  RATES,
  REFUNDS,
  BANK,
} = userPermissions;

export const useSections = ({
  system,
  systemAccess,
}: {
  system: ISystem;
  systemAccess: number;
}) => {
  const { navigationData } = useNavigationContext();

  const totalBillingIssues = useMemo(() => {
    return (
      (navigationData?.totalOfbillingIssues ?? 0) +
      (navigationData?.pending_charges ?? 0) +
      (Boolean(navigationData?.billing_warning) ? 1 : 0)
    );
  }, [navigationData]);

  const organizationInfo = useOrganization();

  // Branded App only exists for organizations whose partner has a white label.
  // Non-suspending, so the sidebar renders immediately and gains the entry
  // once the answer arrives.
  const { data: organizationDetail } = useOrganizationDetailRelayQuery(
    organizationInfo?.id,
    { enabled: Boolean(organizationInfo?.id) }
  );
  const hasBrandedApp = Boolean(organizationDetail?.hasBrandedApp);

  const access = findAccess(organizationInfo?.access || systemAccess);
  const { canAccess: canAccessChat } = useChatAccess();
  const { data: supportChatUnread } = useSupportChatUnread(canAccessChat);

  return useMemo(() => {
    return [
      {
        items: [
          {
            title: 'Dashboard',
            path: paths.dashboard.index,
            icon: (
              <SvgIcon fontSize="small">
                <DashboardIcon />
              </SvgIcon>
            ),
          },
          access.includes(USERS) && {
            title: 'Users',
            path: '/users',
            icon: (
              <SvgIcon fontSize="small">
                <PeopleIcon />
              </SvgIcon>
            ),
          },
          access.includes(RENTAL) && {
            title: 'Rentals',
            path: '/rentals',
            icon: (
              <SvgIcon fontSize="small">
                <LockClockIcon />
              </SvgIcon>
            ),
          },
          access.includes(PHYSICAL) && {
            title: 'Hardware',
            path: '/hardware',
            icon: (
              <SvgIcon fontSize="small">
                <BikeScooterIcon />
              </SvgIcon>
            ),
          },
          access.includes(LOCATIONS) && {
            title: 'Locations',
            path: '/locations',
            icon: (
              <SvgIcon fontSize="small">
                <LocationOnIcon />
              </SvgIcon>
            ),
          },
          access.includes(MEMBERSHIPS) && {
            title: 'Memberships',
            path: '/memberships',
            icon: (
              <Badge
                badgeContent={
                  navigationData?.user_membership_pending
                    ? navigationData?.user_membership_pending
                    : navigationData?.membership_warning
                    ? 1
                    : 0
                }
                color="error"
              >
                <SvgIcon fontSize="small">
                  <CardMembershipIcon />
                </SvgIcon>
              </Badge>
            ),
          },
          access.includes(SUPPORT) && {
            title: 'Support Tickets',
            path: '/problems',
            icon: (
              <Badge badgeContent={navigationData?.support_open} color="error">
                <SvgIcon fontSize="small">
                  <SupportAgentIcon />
                </SvgIcon>
              </Badge>
            ),
          },
          canAccessChat && {
            title: 'Support Chat',
            path: paths.support,
            icon: (
              <Badge badgeContent={supportChatUnread ?? 0} color="error">
                <SvgIcon fontSize="small">
                  <ChatIcon />
                </SvgIcon>
              </Badge>
            ),
          },
          access.includes(MAINTENANCE) && {
            title: 'Maintenance',
            path: '/maintenance',
            icon: (
              <Badge badgeContent={navigationData?.maintenance_open} color="error">
                <SvgIcon fontSize="small">
                  <BuildIcon />
                </SvgIcon>
              </Badge>
            ),
          },
          system.billing &&
            !Boolean(organizationInfo?.id) &&
            access.some((perm) => [RATES, REFUNDS].includes(perm)) && {
              title: 'Billing',
              path: '/billing',
              icon: (
                <Badge badgeContent={totalBillingIssues} color="error">
                  <SvgIcon fontSize="small">
                    <AttachMoneyIcon />
                  </SvgIcon>
                </Badge>
              ),
            },
          Boolean(organizationInfo?.id) && {
            title: 'Coupons',
            path: '/organization/billing/coupons',
            icon: (
              <SvgIcon fontSize="small">
                <LocalOfferIcon />
              </SvgIcon>
            ),
          },
          Boolean(organizationInfo?.id) &&
            hasBrandedApp && {
              title: 'Branded App',
              path: '/branded-app',
              icon: (
                <SvgIcon fontSize="small">
                  <PhoneIphoneIcon />
                </SvgIcon>
              ),
              items: [
                {
                  title: 'Merchant Locations',
                  path: '/branded-app/merchant-locations',
                },
              ],
            },
          Boolean(organizationInfo?.id) && {
            title: 'Systems',
            path: '/systems',
            icon: (
              <Badge badgeContent={0} color="error">
                <SvgIcon fontSize="small">{<WysiwygIcon />}</SvgIcon>
              </Badge>
            ),
          },
          // Same permission admin_api requires to list a system's documents.
          // Documents belong to a system; organizations have none.
          access.includes(BASIC) &&
            !Boolean(organizationInfo?.id) && {
              title: 'Documents',
              path: paths.documents,
              icon: (
                <SvgIcon fontSize="small">
                  <DescriptionIcon />
                </SvgIcon>
              ),
            },
          access.includes(BANK) && {
            title: 'Settings',
            path: '/settings',
            icon: (
              <Badge
                badgeContent={navigationData?.settings_warning ? 1 : 0}
                color="error"
              >
                <SvgIcon fontSize="small">{<SettingsIcon />}</SvgIcon>
              </Badge>
            ),
          },
        ].filter(Boolean),
      },
    ];
  }, [
    navigationData,
    access,
    system.billing,
    totalBillingIssues,
    organizationInfo,
    hasBrandedApp,
    supportChatUnread,
    canAccessChat,
  ]);
};
